import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// Inactivity timeout in milliseconds (20 minutes)
const INACTIVITY_TIMEOUT = 20 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Configure axios defaults
  axios.defaults.withCredentials = true;
  axios.defaults.baseURL = API_BASE;

  // Initialize JWT token from localStorage on app start
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('✅ JWT token restored from localStorage');
    }
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async (retryCount = 0) => {
    try {
      // Ensure token is in headers before making request
      const token = localStorage.getItem('auth_token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      console.log('🔍 Checking auth status... (attempt:', retryCount + 1, ')');
      const response = await axios.get('/api/auth/me');
      console.log('✅ Auth check successful:', response.data);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.log('❌ Auth check error:', error.response?.status, error.message);
      // If unauthorized and we haven't retried, try once more after a delay
      if (error.response?.status === 401 && retryCount < 2) {
        console.log(`Auth check failed, retrying (${retryCount + 1}/2)...`);
        await new Promise(resolve => setTimeout(resolve, 500));
        return await checkAuthStatus(retryCount + 1);
      }
      setUser(null);
      return null;
    } finally {
      if (retryCount === 0) {
        setLoading(false);
        setInitialized(true);
      }
    }
  };

  const login = async (loginData) => {
    try {
      const response = await axios.post('/api/auth/login', loginData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      // Clear stale cache on new login to ensure fresh data
      localStorage.removeItem('offline_data_cache');
      console.log('🧹 Cleared offline cache on login');
      
      // Store JWT token for authentication
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
        // Set token in axios defaults for future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        console.log('✅ JWT token stored');
      }
      
      // Set user immediately from response - this is the key!
      if (response.data.user) {
        setUser(response.data.user);
        console.log('✅ Login successful, user set:', response.data.user);
      }
      
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Login error:', error);
      const message = error.response?.data?.detail || error.message || 'Login failed';
      return { success: false, error: message };
    }
  };

  const signup = async (signupData) => {
    try {
      const response = await axios.post('/api/auth/signup', signupData);
      console.log('✅ Signup successful:', response.data.user);
      
      // Just return success - no auto-login
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Signup error:', error);
      const message = error.response?.data?.detail || error.message || 'Signup failed';
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
      // Clear JWT token and axios header
      localStorage.removeItem('auth_token');
      localStorage.removeItem('last_activity_time');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      return { success: true };
    } catch (error) {
      // Even if logout fails on server, clear local state and token
      localStorage.removeItem('auth_token');
      localStorage.removeItem('last_activity_time');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      return { success: true };
    }
  };

  // Auto-logout due to inactivity
  const performAutoLogout = useCallback(async () => {
    console.log('⏰ Auto-logout triggered due to inactivity');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('last_activity_time');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    // Redirect to home page
    window.location.href = '/?session_expired=true';
  }, []);

  // Reset inactivity timer on user activity
  const resetInactivityTimer = useCallback(() => {
    if (!user) return; // Only track activity for logged-in users
    
    lastActivityRef.current = Date.now();
    localStorage.setItem('last_activity_time', Date.now().toString());
    
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    // Set new timer
    inactivityTimerRef.current = setTimeout(() => {
      performAutoLogout();
    }, INACTIVITY_TIMEOUT);
  }, [user, performAutoLogout]);

  // Check if session has expired on page load/focus
  const checkSessionExpiry = useCallback(() => {
    const lastActivity = localStorage.getItem('last_activity_time');
    if (lastActivity && user) {
      const elapsed = Date.now() - parseInt(lastActivity, 10);
      if (elapsed > INACTIVITY_TIMEOUT) {
        console.log('⏰ Session expired during inactivity');
        performAutoLogout();
        return true;
      }
    }
    return false;
  }, [user, performAutoLogout]);

  // Set up activity listeners when user is logged in
  useEffect(() => {
    if (!user) {
      // Clear timer when user logs out
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      return;
    }

    // Check if session already expired
    if (checkSessionExpiry()) return;

    // Activity events to track
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Throttled activity handler (update at most every 30 seconds to reduce overhead)
    let lastUpdate = 0;
    const throttledResetTimer = () => {
      const now = Date.now();
      if (now - lastUpdate > 30000) { // 30 seconds throttle
        lastUpdate = now;
        resetInactivityTimer();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledResetTimer, { passive: true });
    });

    // Handle page visibility change (user comes back to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSessionExpiry();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle window focus (user comes back to window)
    const handleFocus = () => {
      checkSessionExpiry();
    };
    window.addEventListener('focus', handleFocus);

    // Start the initial timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledResetTimer);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [user, resetInactivityTimer, checkSessionExpiry]);

  const getCaptcha = async () => {
    try {
      // Use absolute URL to ensure it works even before axios baseURL is fully configured
      const apiUrl = API_BASE || process.env.REACT_APP_BACKEND_URL || '';
      const captchaUrl = `${apiUrl}/api/auth/captcha`;
      console.log('🔐 Fetching CAPTCHA from:', captchaUrl);
      const response = await axios.get(captchaUrl);
      console.log('✅ CAPTCHA received:', response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ CAPTCHA fetch error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return { success: false, error: error.response?.data?.detail || 'Failed to get captcha' };
    }
  };

  const value = {
    user,
    loading,
    initialized,
    login,
    signup,
    logout,
    getCaptcha,
    checkAuthStatus,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'sub-admin',
    isHeadteacher: user?.role === 'headteacher',
    isTeacher: user?.role === 'teacher',
    isSISO: user?.role === 'siso',
    isDirector: user?.role === 'director',
    isDDSupervision: user?.role === 'dd_supervision',
    isSecretary: user?.role === 'secretary',
    isDirectorate: ['director', 'dd_supervision', 'secretary'].includes(user?.role),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};